/**
 * Acrescenta uma linha de comentário a um evento de cobrança.
 *
 * ACRESCENTA, não substitui: o backend acumula o texto e a tela mostra o último com todos no
 * tooltip. O evento passa por várias mãos ao longo do prazo, e trocar o texto apagaria o
 * histórico de quem escreveu antes — que é o que alguém procura depois para entender a demora.
 *
 * Vale para evento JÁ RESOLVIDO também: só concluir e cancelar ficam bloqueados fora de pendente.
 * Poder escrever numa ação encerrada é como se explica depois o que aconteceu com ela.
 */
import { useState } from 'react';
import { FaCommentDots, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { InvoiceBillingEventRow, updateInvoiceBillingEvent } from '../services/billingApi';

interface Props {
    invoiceRef: string | number;
    event: InvoiceBillingEventRow;
    onClose: () => void;
    onAdded: () => void;
    zIndexClass?: string;
}

const AddBillingEventCommentModal: React.FC<Props> = ({ invoiceRef, event, onClose, onAdded, zIndexClass = 'z-[60]' }) => {
    const [text, setText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        setSubmitting(true);
        try {
            await updateInvoiceBillingEvent(invoiceRef, event.id, { notes: text.trim() });
            toast.success('Comentário adicionado.');
            onAdded();
        } catch (e: any) {
            toast.error(e?.message || 'Não foi possível adicionar o comentário');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${zIndexClass} p-4`}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <FaCommentDots className="text-blue-500" /> Adicionar comentário
                    </h2>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes size={20} /></button>
                </div>

                <p className="text-xs text-gray-500 mb-3">
                    {event.billingEventName || event.billingEventRef}
                    {' · '}o texto é ACRESCENTADO aos comentários existentes.
                </p>

                <label className="block text-sm font-medium mb-1" htmlFor="comentario-evento">
                    Comentário
                </label>
                <textarea
                    id="comentario-evento"
                    className="w-full text-sm border rounded p-2 mb-3 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    rows={3}
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder="O que aconteceu com esta ação"
                />

                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                        Voltar
                    </button>
                    {/* sem texto não há o que acrescentar, e o backend recusaria o PATCH vazio —
                        melhor o botão dizer isso antes da viagem */}
                    <button onClick={submit} disabled={submitting || !text.trim()}
                        className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                        {submitting ? 'Salvando...' : 'Adicionar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddBillingEventCommentModal;
